import React, { useEffect, useState } from 'react';
import '../../styles/admin/Forms.css';
import formService from '../../services/formService';
import { Trash2, Edit, Eye } from 'lucide-react';

export default function AdminForms() {
  const [activeTab, setActiveTab] = useState('create');
  const [forms, setForms] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editingForm, setEditingForm] = useState(null);
  const [selectedFormForResponses, setSelectedFormForResponses] = useState('');
  const [selectedUserResponse, setSelectedUserResponse] = useState(null);
  const [newForm, setNewForm] = useState({
    title: '',
    intro: '',
    fields: [{ label: 'How was today?', type: 'text', required: false, placeholder: 'Share your takeaway' }]
  });

  const loadForms = async () => {
    try {
      setLoading(true);
      const res = await formService.getFormsAdmin();
      if (res.success) setForms(res.forms || []);
    } catch (err) {
      setError('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const loadResponses = async () => {
    try {
      const res = await formService.getResponsesAdmin();
      if (res.success) setResponses(res.responses || []);
    } catch (err) {
      console.error('Failed to load responses', err);
    }
  };

  useEffect(() => {
    loadForms();
    loadResponses();
  }, []);

  const updateField = (index, updates) => {
    const targetForm = editingForm || newForm;
    const setTargetForm = editingForm ? setEditingForm : setNewForm;
    
    setTargetForm((prev) => {
      const fields = [...prev.fields];
      fields[index] = { ...fields[index], ...updates };
      return { ...prev, fields };
    });
  };

  const addField = () => {
    const targetForm = editingForm || newForm;
    const setTargetForm = editingForm ? setEditingForm : setNewForm;
    
    setTargetForm((prev) => ({
      ...prev,
      fields: [...prev.fields, { label: '', type: 'text', required: false, placeholder: '' }]
    }));
  };

  const removeField = (index) => {
    const targetForm = editingForm || newForm;
    const setTargetForm = editingForm ? setEditingForm : setNewForm;
    
    setTargetForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleCreate = async () => {
    if (!newForm.title.trim()) {
      setError('Form title is required');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await formService.createForm(newForm);
      setMessage('Form created successfully');
      setNewForm({ title: '', intro: '', fields: [{ label: 'How was today?', type: 'text', required: false, placeholder: 'Share your takeaway' }] });
      await loadForms();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create form');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (form) => {
    setEditingForm({ ...form });
    setActiveTab('create');
  };

  const handleUpdate = async () => {
    if (!editingForm.title.trim()) {
      setError('Form title is required');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await formService.updateForm(editingForm.id, editingForm);
      setMessage('Form updated successfully');
      setEditingForm(null);
      await loadForms();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update form');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) {
      return;
    }
    try {
      await formService.deleteForm(formId);
      setMessage('Form deleted successfully');
      await loadForms();
    } catch (err) {
      setError('Failed to delete form');
    }
  };

  const cancelEdit = () => {
    setEditingForm(null);
  };

  const currentForm = editingForm || newForm;
  const setCurrentForm = editingForm ? setEditingForm : setNewForm;

  // Filter responses by selected form
  const filteredResponses = selectedFormForResponses 
    ? responses.filter(r => r.form_id === parseInt(selectedFormForResponses))
    : [];

  // Get unique users from filtered responses
  const uniqueUsers = filteredResponses.reduce((acc, response) => {
    const userKey = response.email || response.user_id || 'Anonymous';
    if (!acc.find(u => u.key === userKey)) {
      acc.push({
        key: userKey,
        email: response.email,
        user_id: response.user_id,
        responses: filteredResponses.filter(r => (r.email || r.user_id) === userKey)
      });
    }
    return acc;
  }, []);

  return (
    <div className="admin-forms">
      <div className="forms-header">
        <div>
          <h1>Forms Management</h1>
          <p>Create, manage, and view responses for program forms.</p>
        </div>
      </div>

      {message && <div className="af-alert success">{message}</div>}
      {error && <div className="af-alert error">{error}</div>}

      {/* Tab Navigation */}
      <div className="af-tabs">
        <button 
          className={`af-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          {editingForm ? 'Edit Form' : 'Create Form'}
        </button>
        <button 
          className={`af-tab ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          Manage Forms
        </button>
        <button 
          className={`af-tab ${activeTab === 'responses' ? 'active' : ''}`}
          onClick={() => setActiveTab('responses')}
        >
          View Responses
        </button>
      </div>

      {/* Create/Edit Form Tab */}
      {activeTab === 'create' && (
        <div className="af-grid">
          <div className="af-card">
            <h3>{editingForm ? 'Edit Form' : 'Create New Form'}</h3>
            {editingForm && (
              <div className="af-edit-notice">
                <p>Editing: <strong>{editingForm.title}</strong></p>
                <button className="btn-secondary" onClick={cancelEdit}>Cancel Edit</button>
              </div>
            )}
            
            <label>Title</label>
            <input 
              value={currentForm.title} 
              onChange={(e) => setCurrentForm({ ...currentForm, title: e.target.value })} 
              placeholder="Daily reflection" 
            />

            <label>Intro text (shown above fields)</label>
            <textarea 
              rows="2" 
              value={currentForm.intro} 
              onChange={(e) => setCurrentForm({ ...currentForm, intro: e.target.value })} 
              placeholder="Share guidance for the user" 
            />

            <div className="af-fields">
              <div className="af-fields-header">
                <span>Fields</span>
                <button className="btn-secondary" onClick={addField}>Add Field</button>
              </div>

              {currentForm.fields.map((field, index) => (
                <div key={index} className="af-field-row">
                  <label className="af-checkbox">
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  <div className="af-row">
                    <label>Label</label>
                    <input value={field.label} onChange={(e) => updateField(index, { label: e.target.value })} />
                  </div>
                  <div className="af-row">
                    <label>Type</label>
                    <select value={field.type} onChange={(e) => updateField(index, { type: e.target.value })}>
                      <option value="text">Short text</option>
                      <option value="textarea">Long answer</option>
                      <option value="number">Number</option>
                    </select>
                  </div>
                  <div className="af-row">
                    <label>Placeholder</label>
                    <input value={field.placeholder || ''} onChange={(e) => updateField(index, { placeholder: e.target.value })} />
                  </div>
                  <button className="btn-link danger" onClick={() => removeField(index)}>Remove</button>
                </div>
              ))}
            </div>

            <div className="af-form-actions">
              <button className="btn-secondary" onClick={addField}>Add Field</button>
              <button 
                className="btn-primary" 
                onClick={editingForm ? handleUpdate : handleCreate} 
                disabled={saving}
              >
                {saving ? 'Saving...' : editingForm ? 'Update Form' : 'Create Form'}
              </button>
            </div>
          </div>

          {/* Form Preview */}
          <div className="af-card">
            <h3>Form Preview</h3>
            {currentForm.title || currentForm.intro || currentForm.fields.length > 0 ? (
              <div className="af-form-preview">
                {currentForm.title && (
                  <h4 className="af-preview-title">{currentForm.title}</h4>
                )}
                {currentForm.intro && (
                  <p className="af-preview-intro">{currentForm.intro}</p>
                )}
                <div className="af-preview-fields">
                  {currentForm.fields.map((field, index) => (
                    <div key={index} className="af-preview-field">
                      <label className="af-preview-label">
                        {field.label}
                        {field.required && <span className="af-required">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea 
                          placeholder={field.placeholder || ''} 
                          className="af-preview-input"
                          rows="3"
                          disabled
                        />
                      ) : field.type === 'number' ? (
                        <input 
                          type="number" 
                          placeholder={field.placeholder || ''} 
                          className="af-preview-input"
                          disabled
                        />
                      ) : (
                        <input 
                          type="text" 
                          placeholder={field.placeholder || ''} 
                          className="af-preview-input"
                          disabled
                        />
                      )}
                    </div>
                  ))}
                </div>
                <button className="af-preview-submit" disabled>Submit</button>
              </div>
            ) : (
              <div className="af-hint">Start building your form to see the preview</div>
            )}
          </div>
        </div>
      )}

      {/* Manage Forms Tab */}
      {activeTab === 'manage' && (
        <div className="af-card">
          <h3>Saved Forms</h3>
          {loading ? (
            <div className="af-hint">Loading...</div>
          ) : forms.length === 0 ? (
            <div className="af-hint">No forms created yet.</div>
          ) : (
            <div className="af-forms-grid">
              {forms.map((form) => (
                <div key={form.id} className="af-form-card">
                  <div className="af-form-header">
                    <h4>{form.title}</h4>
                    <span className="af-form-id">#{form.id}</span>
                  </div>
                  <p className="af-form-intro">{form.intro || 'No description'}</p>
                  <div className="af-form-meta">
                    <span>{form.fields?.length || 0} field{(form.fields?.length || 0) !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="af-form-actions">
                    <button 
                      className="btn-icon" 
                      onClick={() => handleEdit(form)}
                      title="Edit form"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon danger" 
                      onClick={() => handleDelete(form.id)}
                      title="Delete form"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Responses Tab */}
      {activeTab === 'responses' && (
        <div className="af-responses-container">
          <div className="af-card">
            <h3>Form Responses</h3>
            
            <div className="af-response-filters">
              <div className="af-row">
                <label>Select Form</label>
                <select 
                  value={selectedFormForResponses} 
                  onChange={(e) => {
                    setSelectedFormForResponses(e.target.value);
                    setSelectedUserResponse(null);
                  }}
                >
                  <option value="">Choose a form to view responses</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.title} (#{form.id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedFormForResponses && (
              <div className="af-responses-grid">
                <div className="af-users-list">
                  <h4>Users ({uniqueUsers.length})</h4>
                  {uniqueUsers.length === 0 ? (
                    <div className="af-hint">No responses for this form yet.</div>
                  ) : (
                    <div className="af-users-container">
                      {uniqueUsers.map((user) => (
                        <div 
                          key={user.key}
                          className={`af-user-item ${selectedUserResponse?.key === user.key ? 'active' : ''}`}
                          onClick={() => setSelectedUserResponse(user)}
                        >
                          <div className="af-user-info">
                            <strong>{user.email || `User ${user.user_id}` || 'Anonymous'}</strong>
                            <span className="af-response-count">{user.responses.length} response{user.responses.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="af-response-details">
                  {selectedUserResponse ? (
                    <div>
                      <h4>Responses from {selectedUserResponse.email || `User ${selectedUserResponse.user_id}` || 'Anonymous'}</h4>
                      <div className="af-responses-list">
                        {selectedUserResponse.responses.map((response) => (
                          <div key={response.id} className="af-response-item">
                            <div className="af-response-header">
                              <span className="af-response-date">
                                {new Date(response.created_at).toLocaleString()}
                              </span>
                              {response.program_id && (
                                <span className="af-response-program">
                                  Program {response.program_id} - Day {response.day_number || '-'}
                                </span>
                              )}
                            </div>
                            <div className="af-response-content">
                              {Object.entries(response.responses || {}).map(([question, answer]) => (
                                <div key={question} className="af-response-qa">
                                  <strong>{question}:</strong>
                                  <span>{String(answer)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="af-hint">Select a user to view their responses</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
